import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type {
  ChecklistDefinitionVersion,
  ChecklistJobTypeLink,
  ChecklistTemplate,
  CreateChecklistTemplateDto,
  UpdateChecklistTemplateDto,
  PaginatedResponse,
} from '@/types/settings';

interface ChecklistTemplateListParams {
  status?: string;
  jobType?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export function useChecklistTemplates(params?: ChecklistTemplateListParams) {
  return useApiQuery<PaginatedResponse<ChecklistTemplate>>({
    queryKey: [...settingsKeys.checklists(), params ?? {}],
    queryFn: () =>
      apiGet<PaginatedResponse<ChecklistTemplate>>(
        operationsClient,
        settingsEndpoints.checklistTemplates(),
        params ? { params } : undefined,
      ),
    showErrorToast: false,
    staleTime: 30_000,
  });
}

export function useChecklistTemplate(id: string | null) {
  return useApiQuery<ChecklistTemplate>({
    queryKey: settingsKeys.checklistTemplate(id ?? ''),
    queryFn: () =>
      apiGet<ChecklistTemplate>(operationsClient, settingsEndpoints.checklistTemplate(id!)),
    enabled: !!id,
  });
}

export function useChecklistTemplateVersions(id: string | null) {
  return useApiQuery<ChecklistDefinitionVersion[]>({
    queryKey: settingsKeys.checklistTemplateVersions(id ?? ''),
    queryFn: () =>
      apiGet<ChecklistDefinitionVersion[]>(
        operationsClient,
        settingsEndpoints.checklistTemplateVersions(id!),
      ),
    enabled: !!id,
  });
}

export function useCreateChecklistTemplate() {
  const queryClient = useQueryClient();
  return useApiMutation<ChecklistTemplate, CreateChecklistTemplateDto>({
    mutationFn: (dto) =>
      apiPost<ChecklistTemplate, CreateChecklistTemplateDto>(
        operationsClient,
        settingsEndpoints.checklistTemplates(),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.checklists() });
      toast.success('Checklist definition created');
    },
  });
}

export function useUpdateChecklistTemplate() {
  const queryClient = useQueryClient();
  return useApiMutation<ChecklistTemplate, { id: string } & UpdateChecklistTemplateDto>({
    mutationFn: ({ id, ...dto }) =>
      apiPut<ChecklistTemplate, UpdateChecklistTemplateDto>(
        operationsClient,
        settingsEndpoints.checklistTemplate(id),
        dto,
      ),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.checklists() });
      void queryClient.invalidateQueries({
        queryKey: settingsKeys.checklistTemplate(vars.id),
      });
      void queryClient.invalidateQueries({
        queryKey: settingsKeys.checklistTemplateVersions(vars.id),
      });
      toast.success('Checklist definition updated');
    },
  });
}

export function useArchiveChecklistTemplate() {
  const queryClient = useQueryClient();
  return useApiMutation<ChecklistTemplate, string>({
    mutationFn: (id) =>
      apiPost<ChecklistTemplate>(operationsClient, settingsEndpoints.checklistTemplateArchive(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.checklists() });
      toast.success('Checklist archived');
    },
  });
}

export function useDuplicateChecklistTemplate() {
  const queryClient = useQueryClient();
  return useApiMutation<ChecklistTemplate, string>({
    mutationFn: (id) =>
      apiPost<ChecklistTemplate>(
        operationsClient,
        settingsEndpoints.checklistTemplateDuplicate(id),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.checklists() });
      toast.success('Checklist duplicated');
    },
  });
}

export function useRestoreChecklistTemplate() {
  const queryClient = useQueryClient();
  return useApiMutation<ChecklistTemplate, string>({
    mutationFn: (id) =>
      apiPost<ChecklistTemplate>(operationsClient, settingsEndpoints.checklistTemplateRestore(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.checklists() });
      toast.success('Checklist restored');
    },
  });
}

export function useLinkChecklistTemplateJobType() {
  const queryClient = useQueryClient();
  return useApiMutation<
    ChecklistJobTypeLink,
    {
      checklistDefinitionId: string;
      jobTypeId: string;
      blocksCompletion?: boolean;
      sortOrder?: number;
    }
  >({
    mutationFn: ({ checklistDefinitionId, ...dto }) =>
      apiPost<ChecklistJobTypeLink, typeof dto>(
        operationsClient,
        settingsEndpoints.checklistTemplateJobTypes(checklistDefinitionId),
        dto,
      ),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.checklists() });
      void queryClient.invalidateQueries({
        queryKey: settingsKeys.checklistTemplate(vars.checklistDefinitionId),
      });
    },
  });
}

export function useUpdateChecklistTemplateJobTypeLink() {
  const queryClient = useQueryClient();
  return useApiMutation<
    ChecklistJobTypeLink,
    {
      checklistDefinitionId: string;
      jobTypeId: string;
      blocksCompletion?: boolean;
      sortOrder?: number;
    }
  >({
    mutationFn: ({ checklistDefinitionId, jobTypeId, blocksCompletion, sortOrder }) =>
      apiPatch<ChecklistJobTypeLink, { blocksCompletion?: boolean; sortOrder?: number }>(
        operationsClient,
        settingsEndpoints.checklistTemplateJobType(checklistDefinitionId, jobTypeId),
        { blocksCompletion, sortOrder },
      ),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.checklists() });
      void queryClient.invalidateQueries({
        queryKey: settingsKeys.checklistTemplate(vars.checklistDefinitionId),
      });
    },
  });
}

export function useUnlinkChecklistTemplateJobType() {
  const queryClient = useQueryClient();
  return useApiMutation<
    unknown,
    {
      checklistDefinitionId: string;
      jobTypeId: string;
    }
  >({
    mutationFn: ({ checklistDefinitionId, jobTypeId }) =>
      apiDelete(
        operationsClient,
        settingsEndpoints.checklistTemplateJobType(checklistDefinitionId, jobTypeId),
      ),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.checklists() });
      void queryClient.invalidateQueries({
        queryKey: settingsKeys.checklistTemplate(vars.checklistDefinitionId),
      });
    },
  });
}
